import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Container, Paper, Dialog, DialogContent, DialogActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReadingTestUI from '../components/IELTS/ReadingTestUI';
import TestModeModal from '../components/IELTS/TestModeModal';

const PageContainer = styled(Container)(({ theme }) => ({
  padding: '24px',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
}));

const InstructionsContainer = styled(Paper)(({ theme }) => ({
  padding: '24px',
  marginBottom: '24px',
  borderRadius: '12px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
}));

const StartButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#996CFE',
  color: 'white',
  padding: '10px 24px',
  fontSize: '16px',
  '&:hover': {
    backgroundColor: '#7C4DFF',
  },
}));

const ResultDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '12px',
    maxWidth: '600px',
    width: '100%',
  },
}));

// Fallback test data if none provided from location state
const fallbackTestData = {
  id: 'sample-reading-1',
  title: 'IELTS Academic Reading Sample Test',
  category_name: 'Reading',
  total_time: 60,
  test_type: 'Academic',
  sections: [
    {
      id: 'section-1',
      title: 'How the mind ages',
      part: 1,
      passage_text: `
        <h2>How the mind ages</h2>
        <p>The way mental function changes is largely determined by three factors-mental lifestyle, the impact of chronic disease and flexibility of the mind.</p>
        <p>Experiments have shown that younger monkeys consistently outperform their older colleagues on memory tests. Formerly, psychologists concluded that memory and other mental functions in humans deteriorate over time because of changes in the brain. Thus mental decline after young adulthood appeared inevitable. The truth, however, is not quite so simple.</p>
        <p>Stanley Rapoport at the National Institute of Health in the United States measured the flow of blood in the brains of old and young people as they completed different tasks. Since blood flow reflects neural activity, Rapoport could compare which networks of neurons were the same, the neural networks they used were significantly different. The older subjects used different internal strategies to accomplish comparable results at the same time,' Rapoport says. At the Georgia Institute of Technology, psychologist Timothy Salthouse compared a group of fast and accurate typists of college age with another group of fast and accurate typists who were between ages 60 and 80. When tapping speeds were matched, the older typists were actually better typists overall.</p>
      `,
      questions: [
        {
          id: 1,
          part: 1,
          questionNumber: 1,
          type: 'multiplechoice',
          text: 'What does the writer say about the performance of older typists on the test?',
          options: [
            { value: 'A', text: 'They used different motor skills from younger typists.' },
            { value: 'B', text: 'They had been more efficiently trained than younger typists.' },
            { value: 'C', text: 'They used more time-saving techniques than younger typists.' },
            { value: 'D', text: 'They had better concentration skills than younger typists.' }
          ],
          correct_answer: 'B'
        },
        {
          id: 2,
          part: 1,
          questionNumber: 2,
          type: 'multiplechoice',
          text: 'The experiment with the rats showed that',
          options: [
            { value: 'A', text: 'brain structure only changed when the rats were given a familiar toy' },
            { value: 'B', text: 'the rats became anxious after a lengthy period of time alone' },
            { value: 'C', text: 'the rats lived longer then they were part of a social group' },
            { value: 'D', text: 'the rats\'brains expanded or shrank depending on the level of mental activity' }
          ],
          correct_answer: 'D'
        },
        {
          id: 3,
          part: 1,
          questionNumber: 3,
          type: 'multiplechoice',
          text: 'A comparison between adults and children who played chess showed that',
          options: [
            { value: 'A', text: 'the children were as capable as the adults of remembering a series of numbers' },
            { value: 'B', text: 'the children had better recall of the layout of pieces' },
            { value: 'C', text: 'the adults stored memories of chess moves in a more logical manner' },
            { value: 'D', text: 'the adults had clearer memories of chess games they had played' }
          ],
          correct_answer: 'C'
        }
      ]
    },
    {
      id: 'section-2',
      title: 'The Effect of Light on Plant Growth',
      part: 2,
      passage_text: `
        <h2>The Effect of Light on Plant Growth</h2>
        <p>Plants rely on light for their growth and development through a process called photosynthesis. The quality, intensity, duration, and direction of light all affect plant growth in different ways.</p>
        <p>Research has shown that plants respond differently to various wavelengths of light. Blue light (400-500 nm) promotes vegetative growth and leaf development, while red light (600-700 nm) stimulates flowering and fruiting. Green light (500-600 nm) is mostly reflected by plants, which is why they appear green to our eyes.</p>
      `,
      questions: [
        {
          id: 14,
          part: 2,
          questionNumber: 14,
          type: 'multiplechoice',
          text: 'What happens when plants are exposed to blue light?',
          options: [
            { value: 'A', text: 'They start flowering immediately.' },
            { value: 'B', text: 'They develop more leaves and stems.' },
            { value: 'C', text: 'They reflect most of the light.' },
            { value: 'D', text: 'They produce more fruit.' }
          ],
          correct_answer: 'B'
        }
      ]
    },
    {
      id: 'section-3',
      title: 'Urban Planning in the 21st Century',
      part: 3,
      passage_text: `
        <h2>Urban Planning in the 21st Century</h2>
        <p>Urban planning has evolved significantly in response to environmental challenges, technological advancements, and changing social dynamics. Modern urban planners focus on creating sustainable, efficient, and livable cities.</p>
        <p>One key trend is the development of "smart cities" that use data and technology to improve infrastructure, public services, and quality of life. These cities implement sensors and IoT devices to monitor traffic, air quality, energy usage, and waste management, enabling real-time responses to urban challenges.</p>
      `,
      questions: [
        {
          id: 27,
          part: 3,
          questionNumber: 27,
          type: 'multiplechoice',
          text: 'What is a primary focus of modern urban planners according to the passage?',
          options: [
            { value: 'A', text: 'Creating more shopping malls' },
            { value: 'B', text: 'Building taller skyscrapers' },
            { value: 'C', text: 'Developing sustainable and livable cities' },
            { value: 'D', text: 'Increasing tourism revenue' }
          ],
          correct_answer: 'C'
        }
      ]
    }
  ]
};

// Helper to extract correct answers from passage_text HTML
function extractCorrectAnswersFromPassage(passageText) {
  const correctAnswers = {};
  if (!passageText) return correctAnswers;
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(passageText, 'text/html');

  // MCQ and dropdowns (from .mc-question)
  doc.querySelectorAll('.mc-question').forEach((mcq, idx) => {
    const questionText = mcq.getAttribute('question');
    const correct = mcq.getAttribute('correctanswer');
    if (questionText && correct) {
      correctAnswers[questionText.trim()] = correct.trim();
    }
  });

  // Fill-in-the-blank and dropdowns in tables (from .answer-wrapper)
  doc.querySelectorAll('.answer-wrapper').forEach(wrapper => {
    const qNum = wrapper.getAttribute('data-question-number');
    const correct = wrapper.getAttribute('data-correct-answer');
    if (qNum && correct) {
      correctAnswers[qNum.trim()] = correct.trim();
    }
  });

  return correctAnswers;
}

const ReadingTestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [testStarted, setTestStarted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [modeModalOpen, setModeModalOpen] = useState(true);
  const [selectedMode, setSelectedMode] = useState(null); // 'practice' or 'simulation'
  const [selectedSectionCount, setSelectedSectionCount] = useState(3); // default to 3 for simulation
  const [selectedTimeLimit, setSelectedTimeLimit] = useState(60); // default to 60 for simulation
  const [selectedSectionIndices, setSelectedSectionIndices] = useState([0,1,2]); // default to all

  // Get the test duration from location state or use default
  const testDuration = selectedTimeLimit; // Use selected time limit
  const testData = location.state?.testData || fallbackTestData;

  // Handle modal callbacks
  const handleStartPracticeMode = (selectedParts = [1,2,3], timeLimit = 60) => {
    setSelectedMode('practice');
    setSelectedSectionIndices(selectedParts.map(p => p - 1)); // convert to 0-based indices
    setSelectedSectionCount(selectedParts.length);
    setSelectedTimeLimit(timeLimit);
    setModeModalOpen(false);
    setTestStarted(true);
  };
  const handleStartSimulationMode = () => {
    setSelectedMode('simulation');
    setSelectedSectionIndices([0,1,2]);
    setSelectedSectionCount(3);
    setSelectedTimeLimit(60);
    setModeModalOpen(false);
    setTestStarted(true);
  };

  const handleTestComplete = (results) => {
    // Extract all correct answers from all sections
    let correctAnswers = {};
    testData.sections.forEach(section => {
      if (section.passage_text) {
        Object.assign(correctAnswers, extractCorrectAnswersFromPassage(section.passage_text));
      }
    });

    // Debug: log user results and correct answers
    console.log('User results:', results);
    console.log('Correct answers:', correctAnswers);

    // Score the user's answers
    let correctCount = 0;
    results.forEach(ans => {
      // Try to match by question text or question number
      if (
        correctAnswers[ans.question] &&
        ans.answer.toString().trim().toLowerCase() === correctAnswers[ans.question].toString().trim().toLowerCase()
      ) {
        correctCount++;
      } else {
        // Try by question number (if ans.question is a number)
        const qNum = ans.question && ans.question.toString().trim();
        if (
          correctAnswers[qNum] &&
          ans.answer.toString().trim().toLowerCase() === correctAnswers[qNum].toString().trim().toLowerCase()
        ) {
          correctCount++;
        }
      }
    });
    const totalQuestions = results.length;
    const score = totalQuestions > 0 ? (correctCount / totalQuestions) * 9 : 0; // Scale to 9-band IELTS score
    setTestResults({ totalQuestions, correctAnswers: correctCount, score });
    setShowResults(true);
  };

  const findQuestionById = (id) => {
    for (const section of testData.sections) {
      const question = section.questions.find(q => q.id.toString() === id.toString());
      if (question) return question;
    }
    return null;
  };

  const handleCloseResults = () => {
    setShowResults(false);
    navigate('/ielts-dashboard');
  };

  if (!testStarted) {
    return (
      <Box>
        <TestModeModal
          open={modeModalOpen}
          onClose={() => {
            setModeModalOpen(false);
            navigate('/ielts-dashboard');
          }}
          onStartPracticeMode={handleStartPracticeMode}
          onStartSimulationMode={handleStartSimulationMode}
          numParts={3}
          defaultTime={60}
          timeOptions={[60, 55, 50, 45]}
          partLabels={["Section 1 (13-14 questions)", "Section 2 (13-14 questions)", "Section 3 (13-14 questions)"]}
          fullPartsLabel="Full test (3 sections - 60 minutes)"
        />
      </Box>
    );
  }

  if (!testData) {
    return <Typography variant="h6" color="error">No test data found. Please return to the dashboard and try again.</Typography>;
  }

  return (
    <>
      <ReadingTestUI 
        timeLimit={testDuration} 
        onComplete={handleTestComplete} 
        sections={selectedSectionIndices.map(i => testData.sections[i]).filter(Boolean)}
        selectedSectionIndices={selectedSectionIndices}
        answer_sheet={testData.answer_sheet}
        testId={testData.id}
        explanation={testData.explaination}
      />
    </>
  );
};

export default ReadingTestPage; 